import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { importCensusHistory, importKpiHistory } from "../../../../services/clearing/fleet-dashboard";
import type {
  CensusImportOutcome,
  CensusImportResponse,
  KpiHistoryImportOutcome,
  KpiHistoryImportResponse,
} from "../../../../types/chargingStationDashboard";
import { isFleetFailure } from "../../../../types/chargingStationDashboard";
import type { HistoryMonth, KpiHistoryMonth } from "../../../../utils/chargePointHistory";
import { readHistoryFile } from "../../../../utils/chargePointHistory";
import { formatEuro, formatNumber, formatPercent, safeDivide } from "../../../../utils/fleetDerivations";
import { monthLabel } from "../../../../utils/fleetPeriods";

type Outcome = CensusImportOutcome | KpiHistoryImportOutcome;

const OUTCOME: Record<Outcome, { label: string; severity: "success" | "info" | "warning" | "danger" }> = {
  IMPORTED: { label: "Imports", severity: "success" },
  REPLACED_IMPORT: { label: "Replaces earlier import", severity: "info" },
  SKIPPED_SNAPSHOT_EXISTS: { label: "Kept: nightly count exists", severity: "info" },
  SKIPPED_BILLING_EXISTS: { label: "Kept: invoiced figures exist", severity: "info" },
  SKIPPED_NOT_COMPLETE: { label: "Skipped: month not finished", severity: "warning" },
  SKIPPED_DAY_NOT_COMPUTED: { label: "Skipped: recompute first", severity: "warning" },
  REJECTED_INVALID: { label: "Rejected", severity: "danger" },
};

const WRITES: Outcome[] = ["IMPORTED", "REPLACED_IMPORT"];

interface Checked {
  outcome?: Outcome;
  detail?: string | null;
}

type CensusRow = HistoryMonth & Checked;
type KpiRow = KpiHistoryMonth & Checked;

type Stage = "idle" | "reading" | "previewing" | "previewFailed" | "ready" | "importing" | "done";

interface ChargePointHistoryImportProps {
  /** Called after figures were written, so the page can show them. */
  onImported?: (written: number) => void;
  /** Opens the recompute, for months whose days have not been calculated yet. */
  onRecompute?: () => void;
}

/** What the server said about each month, whichever import it came from. */
type MonthOutcomes = Array<{ month: string; outcome: Outcome; detail: string | null }>;

const withOutcomes = <T extends { month: string }>(rows: T[], outcomes: MonthOutcomes | null): Array<T & Checked> => {
  const byMonth = new Map<string, MonthOutcomes[number]>();
  (outcomes ?? []).forEach((m) => byMonth.set(m.month, m));
  return rows.map((r) => ({ ...r, outcome: byMonth.get(r.month)?.outcome, detail: byMonth.get(r.month)?.detail }));
};

const outcomesOf = (response: CensusImportResponse | KpiHistoryImportResponse | null): MonthOutcomes | null =>
  response ? (response.months as MonthOutcomes) : null;

const written = (rows: Checked[]) => rows.filter((r) => r.outcome && WRITES.includes(r.outcome)).length;

/**
 * BOF-76 — imports the history kept by hand in the *Charging Station Dashboard* workbook.
 *
 * - **Charge points** (`monthly-data`): counted nightly only from go-live, and a past day cannot be counted
 *   afterwards, so each earlier month's figure becomes that month's end-of-month count.
 * - **Invoiced clients and service fee** (`KPI_YYYY`): read from the credit-note runs, whose history only
 *   starts with the May 2026 billing; earlier months take the workbook's figures.
 *
 * Two steps on purpose: the file is read and previewed against the server (a dry run says, month by
 * month, what would happen) before anything is written. Uploading a corrected workbook later replaces the
 * earlier import; a nightly count or an invoiced figure is never overwritten.
 */
const ChargePointHistoryImport: React.FC<ChargePointHistoryImportProps> = ({ onImported, onRecompute }) => {
  const upload = useRef<FileUpload>(null);
  const [stage, setStage] = useState<Stage>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [census, setCensus] = useState<CensusRow[]>([]);
  const [kpi, setKpi] = useState<KpiRow[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [writtenCount, setWrittenCount] = useState<number | null>(null);

  const plainCensus = (): HistoryMonth[] => census.map(({ month, total, online }) => ({ month, total, online }));
  const plainKpi = (): KpiHistoryMonth[] =>
    kpi.map(({ month, invoicedClients, serviceFeeCents }) => ({ month, invoicedClients, serviceFeeCents }));

  // PrimeReact does not export its handler event type; only `files` is used.
  const onSelect = async (event: { files: File[] }) => {
    const file = event.files?.[0];
    upload.current?.clear();
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setWrittenCount(null);
    setCensus([]);
    setKpi([]);
    setStage("reading");

    const parsed = await readHistoryFile(file);
    setProblems(parsed.problems);
    if (!parsed.months.length && !parsed.kpi.length) {
      setStage("idle");
      return;
    }
    setCensus(parsed.months);
    setKpi(parsed.kpi);
    await runPreview(parsed.months, parsed.kpi);
  };

  /** Asks the server what an import would do, month by month. Nothing is written. */
  const runPreview = async (months: HistoryMonth[], kpiMonths: KpiHistoryMonth[]) => {
    setError(null);
    setStage("previewing");
    const [censusPreview, kpiPreview] = await Promise.all([
      months.length ? importCensusHistory({ months, dryRun: true }) : null,
      kpiMonths.length ? importKpiHistory({ months: kpiMonths, dryRun: true }) : null,
    ]);
    const failures: string[] = [];
    if (censusPreview && isFleetFailure(censusPreview)) failures.push(censusPreview.message);
    if (kpiPreview && isFleetFailure(kpiPreview)) failures.push(kpiPreview.message);
    if (failures.length) {
      // Keep the months on screen and offer a retry: the file was read fine, the server was not reached.
      setError(failures.join(" "));
      setStage("previewFailed");
      return;
    }
    const dataOf = <T,>(r: { success: true; data: T } | { success: false } | null): T | null => (r && r.success === true ? r.data : null);
    setCensus(withOutcomes(months, outcomesOf(dataOf(censusPreview))));
    setKpi(withOutcomes(kpiMonths, outcomesOf(dataOf(kpiPreview))));
    setStage("ready");
  };

  const toWrite = written(census) + written(kpi);
  const needsRecompute = census.some((r) => r.outcome === "SKIPPED_DAY_NOT_COMPUTED");

  const runImport = async () => {
    setStage("importing");
    setError(null);
    const [censusResult, kpiResult] = await Promise.all([
      written(census) ? importCensusHistory({ months: plainCensus(), dryRun: false }) : null,
      written(kpi) ? importKpiHistory({ months: plainKpi(), dryRun: false }) : null,
    ]);
    let total = 0;
    const messages: string[] = [];
    if (censusResult) {
      if (isFleetFailure(censusResult)) messages.push(censusResult.message);
      else {
        setCensus(withOutcomes(census, outcomesOf(censusResult.data)));
        total += censusResult.data.written;
      }
    }
    if (kpiResult) {
      if (isFleetFailure(kpiResult)) messages.push(kpiResult.message);
      else {
        setKpi(withOutcomes(kpi, outcomesOf(kpiResult.data)));
        total += kpiResult.data.written;
      }
    }
    setWrittenCount(total);
    if (messages.length) {
      setError(messages.join(" "));
      setStage(total ? "done" : "ready");
    } else {
      setStage("done");
    }
    if (total > 0) onImported?.(total);
  };

  const busy = stage === "reading" || stage === "previewing" || stage === "importing";

  const outcomeCell = (r: Checked) =>
    r.outcome ? (
      <span title={r.detail ?? undefined}>
        <Tag value={OUTCOME[r.outcome].label} severity={OUTCOME[r.outcome].severity} />
      </span>
    ) : stage === "previewFailed" ? (
      <span className="text-color-secondary">Not checked</span>
    ) : (
      <span className="text-color-secondary">Checking…</span>
    );

  return (
    <div>
      <p className="mt-0 text-color-secondary line-height-3">
        Upload the <em>cocharge - Charging Station Dashboard</em> workbook (.xlsx), or its <code>monthly-data</code>{" "}
        sheet as .csv. From <code>monthly-data</code> each earlier month's charge-point figure becomes that month's
        end-of-month count; from the <code>KPI_YYYY</code> sheets, invoiced clients and the service fee fill the months
        before the credit-note run history. The dashboard marks these figures as hand-kept. Uploading again later
        replaces the earlier import; a nightly count or an invoiced figure is never overwritten.
      </p>

      <div className="flex flex-wrap align-items-center gap-3 mb-3">
        <FileUpload
          ref={upload}
          mode="basic"
          name="workbook"
          accept=".xlsx,.csv"
          customUpload
          auto
          uploadHandler={onSelect}
          chooseLabel={fileName ? "Choose another file" : "Choose workbook"}
          disabled={busy}
          pt={{ input: { "aria-label": "Workbook file" } }}
        />
        {fileName && <span className="text-sm text-color-secondary">{fileName}</span>}
        {busy && <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="6" />}
      </div>

      {problems.length > 0 && (
        <Message
          severity="warn"
          className="w-full justify-content-start mb-3"
          content={
            <div>
              <div className="font-semibold mb-1">Some rows were not used:</div>
              <ul className="m-0 pl-3">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          }
        />
      )}

      {error && <Message severity="error" className="w-full justify-content-start mb-3" text={error} />}

      {stage === "done" && writtenCount != null && (
        <Message
          severity="success"
          className="w-full justify-content-start mb-3"
          text={`Imported ${writtenCount} month(s). The dashboard now shows them, marked as hand-kept.`}
        />
      )}

      {needsRecompute && stage !== "importing" && (
        <Message
          severity="info"
          className="w-full justify-content-start mb-3"
          content={
            <div className="flex flex-wrap align-items-center gap-2">
              <span>
                Some months have not been calculated yet, so there is no day to attach their charge-point figure to.
                Recompute those months, then upload again.
              </span>
              {onRecompute && <Button label="Recompute first" icon="pi pi-database" size="small" text onClick={onRecompute} />}
            </div>
          }
        />
      )}

      {census.length > 0 && (
        <>
          <div className="font-semibold mb-2">Charge points</div>
          <DataTable
            value={census}
            dataKey="month"
            size="small"
            className="text-sm mb-3"
            scrollable
            scrollHeight="260px"
            pt={{ table: { "aria-label": "Charge-point history to import" } }}
          >
            <Column header="Month" body={(r: CensusRow) => monthLabel(r.month)} />
            <Column header="Total" body={(r: CensusRow) => formatNumber(r.total)} />
            <Column header="Online" body={(r: CensusRow) => formatNumber(r.online)} />
            <Column header="Online rate" body={(r: CensusRow) => formatPercent(safeDivide(r.online, r.total))} />
            <Column header={stage === "done" ? "Result" : "What happens"} body={outcomeCell} />
          </DataTable>
        </>
      )}

      {kpi.length > 0 && (
        <>
          <div className="font-semibold mb-2">Invoiced clients and service fee</div>
          <DataTable
            value={kpi}
            dataKey="month"
            size="small"
            className="text-sm"
            scrollable
            scrollHeight="260px"
            pt={{ table: { "aria-label": "KPI history to import" } }}
          >
            <Column header="Month" body={(r: KpiRow) => monthLabel(r.month)} />
            <Column header="Invoiced clients" body={(r: KpiRow) => formatNumber(r.invoicedClients)} />
            <Column header="Service fee" body={(r: KpiRow) => formatEuro(r.serviceFeeCents, 2)} />
            <Column header={stage === "done" ? "Result" : "What happens"} body={outcomeCell} />
          </DataTable>
        </>
      )}

      {(census.length > 0 || kpi.length > 0) && stage !== "done" && (
        <div className="flex flex-wrap align-items-center gap-3 mt-3">
          <Button
            label={stage !== "ready" ? "Import" : toWrite ? `Import ${toWrite} month${toWrite === 1 ? "" : "s"}` : "Nothing to import"}
            icon="pi pi-upload"
            disabled={stage !== "ready" || !toWrite}
            loading={stage === "importing"}
            onClick={runImport}
          />
          {stage === "previewFailed" && (
            <Button label="Retry preview" icon="pi pi-refresh" outlined onClick={() => runPreview(plainCensus(), plainKpi())} />
          )}
          <span className="text-sm text-color-secondary">
            {stage === "previewFailed"
              ? "The preview failed, so nothing can be imported yet. Nothing has been written."
              : stage === "previewing"
                ? "Checking each month against the server…"
                : "Nothing has been written yet."}
          </span>
        </div>
      )}
    </div>
  );
};

export default ChargePointHistoryImport;
